package dev.lingvo.shared.agent

import dev.lingvo.shared.Role
import dev.lingvo.shared.TokenScope
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject

/**
 * Which server carries a tool.
 *
 * Handlers differ between the two for real reasons — the local server reaches a filesystem and the
 * remote one must never grow one — and this is where that difference is *declared* rather than
 * discovered by an agent at runtime.
 */
@Serializable
enum class Transport(val id: String) {
    /** `lingvo mcp`, in the customer's repository, with the developer's own authority. */
    LOCAL("local"),

    /** `POST /api/v1/mcp`, as an agent connection, under scopes, autonomy and a budget. */
    REMOTE("remote"),
}

/**
 * What calling a tool can do, which is the fact a client needs in order to decide whether to ask a
 * person first.
 *
 * Carries an [id] because it is a wire value the dashboard reads back, and because the three enums
 * it sits beside — [TokenScope], `AgentAutonomy`, `AgentDenial` — all carry one. An enum whose wire
 * spelling is its Kotlin name is an enum nobody can rename.
 *
 * **There is deliberately no `APPROVE`.** Approval is a person's decision, so no class expresses it,
 * no tool declares it and no autonomy level reaches it. See `AgentWrites`.
 */
@Serializable
enum class ToolSafety(val id: String) {
    /** Mutates nothing, anywhere. Free. */
    READ("read"),

    /** Writes only proposals or unapproved captures. Never touches translations or anything a store shows. */
    PROPOSE("propose"),

    /** May write a translation whose state is EMPTY. May never overwrite text. */
    WRITE_EMPTY("write_empty"),

    /** May overwrite a translation that is not APPROVED. */
    WRITE("write"),

    /** Creates or changes keys, files or their metadata. Changes what the product says. */
    STRUCTURAL("structural"),

    /** Reserves credits. Costs real money. */
    SPEND("spend"),
}

/**
 * One tool, declared once, mounted by every transport that carries it.
 *
 * The declaration is deliberately separate from the handler. Handlers differ for real reasons; a
 * *name*, a *description* or a *schema* that differs between transports is how an agent comes to
 * believe two incompatible products share a brand. Before this existed, six tool names were served
 * by both servers and **every one of the six differed** in description, schema, or both — including
 * `list_keys`, which required `project` on one transport and not the other, and `locale_info`, whose
 * local description promised a writing direction that neither server returned.
 */
@Serializable
data class AgentToolSpec(
    val name: String,

    /** One line, imperative, for a tool list a model reads in full. */
    val summary: String,

    val safety: ToolSafety,

    /** Every scope the caller's credential must hold. Empty means the tool is free to anyone. */
    val scopes: Set<TokenScope> = emptySet(),

    /** The membership role the caller's *borrowed* authority must reach. */
    val minimumRole: Role = Role.VIEWER,

    /**
     * JSON Schema for the arguments. **Types are declared, never inferred from a parameter name.**
     *
     * Both previous builders got this wrong in opposite directions. The local one typed a property
     * boolean if it happened to be called `dry_run` or `obsolete_missing` and string otherwise; the
     * remote one typed every property string and marked every property required — so
     * `push_strings.entries` and `propose_plural.forms` were advertised as strings while their
     * handlers did `as? JsonObject`. The schema lied about the two most structured arguments on the
     * surface.
     */
    val input: JsonObject,

    /**
     * JSON Schema for `structuredContent`, or null while the tool returns only text.
     *
     * Spec §1.1 asks for one on every tool. Every handler on both transports today returns an MCP
     * `content` array and no `structuredContent` at all, and advertising an `outputSchema` a client
     * would then wait for is the same class of error as `locale_info` promising a writing direction
     * it never emitted. It is filled in by the change that makes the tool emit the structure, which
     * is also where §9.3's golden file belongs.
     */
    val output: JsonObject? = null,

    /** Calling it twice with the same arguments leaves the same state. */
    val idempotent: Boolean = true,

    /** It accepts `dry_run` and can describe what it would do without doing it. */
    val dryRunnable: Boolean = false,

    val transports: Set<Transport>,
) {
    /** Whether this tool changes anything, derived from its class so the two cannot disagree. */
    val mutates: Boolean get() = safety != ToolSafety.READ
}
